import { send, init } from "@emailjs/browser";
import logger from "../utils/logger.js";

class EmailService {
  constructor() {
    // Get environment variables from import.meta.env (Vite)
    this.serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    this.approvalTemplateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID_APPROVAL;
    this.rejectionTemplateId =
      import.meta.env.VITE_EMAILJS_TEMPLATE_ID_REJECTION;
    this.publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

    // Initialize EmailJS
    if (this.publicKey) {
      init(this.publicKey);
    }

    // Log configuration status
    if (this.isConfigured()) {
      logger.info("EmailJS service initialized successfully");
    } else {
      logger.warn("EmailJS service not configured - emails will not be sent", {
        hasServiceId: !!this.serviceId,
        hasApprovalTemplate: !!this.approvalTemplateId,
        hasRejectionTemplate: !!this.rejectionTemplateId,
        hasPublicKey: !!this.publicKey,
      });
    }
  }

  /**
   * Validates EmailJS configuration
   * @returns {boolean} True if configuration is valid
   */
  isConfigured() {
    return !!(
      this.serviceId &&
      this.approvalTemplateId &&
      this.rejectionTemplateId &&
      this.publicKey
    );
  }

  /**
   * Sends approval email with login credentials
   * @param {string} userEmail - User's email address
   * @param {string} temporaryPassword - Generated temporary password
   * @param {Object} userInfo - Additional user information
   * @returns {Promise<void>}
   */
  async sendApprovalEmail(userEmail, temporaryPassword, userInfo = {}) {
    const startTime = Date.now();

    try {
      // Validate inputs
      if (!userEmail || !temporaryPassword) {
        throw new Error("User email and temporary password are required");
      }

      console.log("EmailJS Configuration Check:", {
        serviceId: this.serviceId,
        approvalTemplateId: this.approvalTemplateId,
        publicKey: this.publicKey ? "configured" : "missing",
        isConfigured: this.isConfigured(),
      });

      if (!this.isConfigured()) {
        const errorMsg = `EmailJS not configured properly. Missing: ${
          !this.serviceId ? "Service ID, " : ""
        }${!this.approvalTemplateId ? "Approval Template, " : ""}${
          !this.publicKey ? "Public Key" : ""
        }`.replace(/, $/, "");

        console.warn(errorMsg, {
          recipient: logger.maskEmail(userEmail),
          hasServiceId: !!this.serviceId,
          hasApprovalTemplate: !!this.approvalTemplateId,
          hasPublicKey: !!this.publicKey,
        });

        // For development, log the email details instead of failing
        if (
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
        ) {
          console.log("📧 DEVELOPMENT MODE - Email would be sent:", {
            to: userEmail,
            subject: "Registration Approved - Welcome!",
            temporaryPassword: temporaryPassword,
            loginUrl: window.location.origin + "/admin/login",
          });
          return {
            status: 200,
            text: "Development mode - email logged to console",
          };
        }

        throw new Error(errorMsg);
      }

      const templateParams = {
        to_email: userEmail,
        from_name: "Mirt Sew",
        to_name: userInfo.fullName || "User",
        temporary_password: temporaryPassword,
        login_url: window.location.origin + "/admin/login",
        user_email: userEmail,
        approval_date: new Date().toLocaleDateString(),
        // Show approval section, hide reset section
        approval_display: "block",
        reset_display: "none",
        reset_link: "",
        email_subject: "Registration Approved - Welcome to MirtSew!",
      };

      console.log("Sending approval email with params:", {
        recipient: logger.maskEmail(userEmail),
        templateId: this.approvalTemplateId,
        serviceId: this.serviceId,
      });

      logger.info("Sending approval email", {
        recipient: logger.maskEmail(userEmail),
        templateId: this.approvalTemplateId,
      });

      const response = await send(
        this.serviceId,
        this.approvalTemplateId,
        templateParams,
      );

      const duration = Date.now() - startTime;
      console.log("Email sent successfully:", response);
      logger.emailEvent("approval", userEmail, "success", {
        responseStatus: response.status,
        duration: `${duration}ms`,
      });

      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error("Email sending failed:", error);
      logger.emailEvent("approval", userEmail, "failed", {
        error: error.message,
        duration: `${duration}ms`,
      });

      throw new Error(`Failed to send approval email: ${error.message}`);
    }
  }

  /**
   * Sends rejection email with admin remark
   * @param {string} userEmail - User's email address
   * @param {string} remark - Admin's rejection reason
   * @param {Object} userInfo - Additional user information
   * @returns {Promise<void>}
   */
  async sendRejectionEmail(userEmail, remark, userInfo = {}) {
    const startTime = Date.now();

    try {
      // Validate inputs
      if (!userEmail || !remark) {
        throw new Error("User email and rejection remark are required");
      }

      console.log("EmailJS Configuration Check:", {
        serviceId: this.serviceId,
        rejectionTemplateId: this.rejectionTemplateId,
        publicKey: this.publicKey ? "configured" : "missing",
        isConfigured: this.isConfigured(),
      });

      if (!this.isConfigured()) {
        const errorMsg = `EmailJS not configured properly. Missing: ${
          !this.serviceId ? "Service ID, " : ""
        }${!this.rejectionTemplateId ? "Rejection Template, " : ""}${
          !this.publicKey ? "Public Key" : ""
        }`.replace(/, $/, "");

        console.warn(errorMsg, {
          recipient: logger.maskEmail(userEmail),
          hasServiceId: !!this.serviceId,
          hasRejectionTemplate: !!this.rejectionTemplateId,
          hasPublicKey: !!this.publicKey,
        });

        // For development, log the email details instead of failing
        if (
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
        ) {
          console.log("📧 DEVELOPMENT MODE - Email would be sent:", {
            to: userEmail,
            subject: "Registration Status Update",
            rejectionReason: remark,
            contactEmail: "admin@example.com",
          });
          return {
            status: 200,
            text: "Development mode - email logged to console",
          };
        }

        throw new Error(errorMsg);
      }

      const templateParams = {
        to_email: userEmail,
        from_name: "Mirt Sew",
        to_name: userInfo.fullName || "User",
        rejection_reason: remark,
        rejection_date: new Date().toLocaleDateString(),
        contact_email: "info@mirtsew.com", // Replace with actual admin email
      };

      console.log("Sending rejection email with params:", {
        recipient: logger.maskEmail(userEmail),
        templateId: this.rejectionTemplateId,
        serviceId: this.serviceId,
      });

      logger.info("Sending rejection email", {
        recipient: logger.maskEmail(userEmail),
        templateId: this.rejectionTemplateId,
        remarkLength: remark.length,
      });

      const response = await send(
        this.serviceId,
        this.rejectionTemplateId,
        templateParams,
      );

      const duration = Date.now() - startTime;
      console.log("Email sent successfully:", response);
      logger.emailEvent("rejection", userEmail, "success", {
        responseStatus: response.status,
        duration: `${duration}ms`,
      });

      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error("Email sending failed:", error);
      logger.emailEvent("rejection", userEmail, "failed", {
        error: error.message,
        duration: `${duration}ms`,
      });

      throw new Error(`Failed to send rejection email: ${error.message}`);
    }
  }

  /**
   * Creates professional email templates for EmailJS
   * @returns {Object} Template configurations
   */
  getEmailTemplates() {
    return {
      approval: {
        name: "Registration Approval",
        subject: "Registration Approved - Welcome!",
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #28a745;">Registration Approved!</h2>
            <p>Dear {{to_name}},</p>
            <p>Congratulations! Your registration has been approved.</p>
            
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
              <h3>Your Login Credentials:</h3>
              <p><strong>Email:</strong> {{user_email}}</p>
              <p><strong>Temporary Password:</strong> {{temporary_password}}</p>
              <p><strong>Login URL:</strong> <a href="{{login_url}}">{{login_url}}</a></p>
            </div>
            
            <p><strong>Important:</strong> You will be required to change your password on first login for security purposes.</p>
            
            <p>If you have any questions, please contact our support team.</p>
            
            <p>Best regards,<br>Registration Team</p>
            <p><small>Approved on: {{approval_date}}</small></p>
          </div>
        `,
      },
      rejection: {
        name: "Registration Rejection",
        subject: "Registration Status Update",
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <h2 style="color: #dc3545;">Registration Update</h2>
            <p>Dear {{to_name}},</p>
            <p>Thank you for your registration submission. After careful review, we regret to inform you that your registration could not be approved at this time.</p>
            
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
              <h3>Reason:</h3>
              <p>{{rejection_reason}}</p>
            </div>
            
            <p>If you believe this decision was made in error or if you have additional information to provide, please contact us at {{contact_email}}.</p>
            
            <p>Thank you for your understanding.</p>
            
            <p>Best regards,<br>Registration Team</p>
            <p><small>Reviewed on: {{rejection_date}}</small></p>
          </div>
        `,
      },
    };
  }

  /**
   * Tests email service configuration and connectivity
   * @returns {Promise<Object>} Test result with status and details
   */
  async testEmailService() {
    try {
      if (!this.isConfigured()) {
        return {
          success: false,
          message: "EmailJS not configured",
          details: {
            hasServiceId: !!this.serviceId,
            hasApprovalTemplate: !!this.approvalTemplateId,
            hasRejectionTemplate: !!this.rejectionTemplateId,
            hasPublicKey: !!this.publicKey,
          },
        };
      }

      // Test with a dummy template to verify connectivity
      const testParams = {
        to_email: "test@example.com",
        to_name: "Test User",
        test_message: "This is a test email",
      };

      logger.info("Testing EmailJS service configuration");

      // Note: This would send a real email in production
      // In a real implementation, you might want to use a test template
      // or have a specific test endpoint

      return {
        success: true,
        message: "EmailJS service is properly configured",
        details: {
          serviceId: this.serviceId,
          approvalTemplateId: this.approvalTemplateId,
          rejectionTemplateId: this.rejectionTemplateId,
          publicKeyConfigured: !!this.publicKey,
        },
      };
    } catch (error) {
      logger.error("EmailJS service test failed", error);
      return {
        success: false,
        message: "EmailJS service test failed",
        error: error.message,
      };
    }
  }

  /**
   * Retry email sending with exponential backoff
   * @param {Function} emailFunction - Email function to retry
   * @param {Array} args - Arguments for the email function
   * @param {number} maxRetries - Maximum number of retries
   * @returns {Promise<any>} Result of email function
   */
  async retryEmailSend(emailFunction, args, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger.info(`Email send attempt ${attempt}/${maxRetries}`);
        return await emailFunction.apply(this, args);
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries) {
          logger.error(`Email send failed after ${maxRetries} attempts`, error);
          break;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt - 1) * 1000;
        logger.warn(
          `Email send attempt ${attempt} failed, retrying in ${delay}ms`,
          {
            error: error.message,
          },
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }

  /**
   * Send password reset email via EmailJS
   * @param {string} userEmail - Recipient email
   * @param {string} resetLink - Full reset URL with token
   */
  async sendPasswordResetEmail(userEmail, resetLink) {
    if (!this.isConfigured()) {
      throw new Error("EmailJS not configured");
    }

    // Reuse the approval template — show reset section, hide approval section
    const templateParams = {
      to_email: userEmail,
      to_name: userEmail,
      from_name: "Mirt Sew",
      reset_link: resetLink,
      reset_display: "block",
      approval_display: "none",
      temporary_password: "",
      login_url: "",
      approval_date: "",
      email_subject: "Password Reset Request - MirtSew",
    };

    await send(
      this.serviceId,
      this.approvalTemplateId,
      templateParams,
      this.publicKey,
    );
  }
}

export default new EmailService();
